import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {ArrowRight, Search, MapPin} from 'lucide-react';
import type { HomeSection } from '@/hooks/useHomeSections';
import { useJobDepositFlow } from '@/hooks/useJobDepositFlow';

const DISPLAY_FONT = "'Bricolage Grotesque', Inter, system-ui, sans-serif";

// Photo cinématographique sous-marine — golden hour, god rays, grain filmique
const HERO_IMAGE = 'https://static.lumi.new/4a/4a039e45eb237ad2e275af47f20173f4.png';

/**
 * HomeHeroSection — Vitrine éditoriale cinématographique
 *
 * Direction artistique :
 *  - Photo plein cadre traitée comme un still de film (Denis Villeneuve)
 *  - Voile sombre dégradé bas → haut pour garantir la lisibilité du H1
 *  - H1 serif-style ultra serré débordant légèrement à gauche (rythme magazine)
 *  - Eyebrow numéroté "01 — Marketplace aquatique" comme signature éditoriale
 *  - Search bar flottante en bas du hero, glassmorphism subtil
 *  - Filet gradient signature en bas pour transition avec la section suivante
 */
export default function HomeHeroSection({ section }: {section: HomeSection;}) {
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  // Flux de dépôt d'annonce recruteur (ouvre directement le formulaire de dépôt).
  const { openModal: openJobDeposit, modal: jobDepositModal } = useJobDepositFlow();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const trimmedKeyword = searchKeyword.trim();
    const trimmedLocation = searchLocation.trim();
    if (trimmedKeyword) params.set('q', trimmedKeyword);
    if (trimmedLocation) params.set('lieu', trimmedLocation);
    const qs = params.toString();
    navigate(qs ? `/offres?${qs}` : '/offres');
  };

  return (
    <section className="relative overflow-hidden bg-[#05070d] min-h-[88vh] flex flex-col">
      {/* ── Photo cinématographique plein cadre ── */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover"
          style={{
            // Léger grading chaud + saturation contenue pour un look filmique
            filter: 'saturate(1.05) contrast(1.08) brightness(0.92)'
          }}
          loading="eager"
          decoding="async" />

      </div>

      {/* ── Voile sombre directionnel (bas → haut) pour ancrer le titre ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
          'linear-gradient(180deg, rgba(5,7,13,0.55) 0%, rgba(5,7,13,0.30) 35%, rgba(5,7,13,0.55) 70%, rgba(5,7,13,0.92) 100%)'
        }} />


      {/* ── Voile latéral subtil pour focus central ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
          'radial-gradient(ellipse at center, transparent 35%, rgba(5,7,13,0.45) 100%)'
        }} />


      {/* ── Touche signature : halo rouge BNSSA en haut-gauche, halo bleu MNS en bas-droite ── */}
      <div
        className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none mix-blend-screen opacity-60"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(circle, rgba(220,38,38,0.55) 0%, transparent 70%)',
          filter: 'blur(60px)'
        }} />

      <div
        className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none mix-blend-screen opacity-50"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(circle, rgba(37,99,235,0.55) 0%, transparent 70%)',
          filter: 'blur(70px)'
        }} />


      {/* ── Grain filmique subtil ── */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
        aria-hidden="true"
        style={{
          backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
          backgroundSize: '200px 200px'
        }} />


      {/* ── Contenu ── */}
      <div className="relative flex-1 flex flex-col justify-center max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-16 sm:pb-20">
        {/* H1 — éditorial XL, centré sur mobile, aligné à gauche sur desktop */}
        <h1
          className="text-white font-black leading-[0.94] tracking-[-0.045em] mb-8 sm:mb-10 text-center sm:text-left text-balance"
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 'clamp(2.85rem, 7.8vw, 6.75rem)',
            textShadow: '0 2px 28px rgba(0,0,0,0.4)'
          }}
          dangerouslySetInnerHTML={{
            __html: section.title.
            replace(/BNSSA/g, '<span class="text-red-400">BNSSA</span>').
            replace(/MNS/g, '<span class="text-blue-300">MNS</span>')
          }} />


        {/* Intro — pull-quote style éditorial, centrée sur mobile */}
        <div
          className="text-white/85 max-w-2xl mx-auto sm:mx-0 leading-[1.55] mb-10 sm:mb-14 [&_p]:m-0 font-light text-center sm:text-left"
          style={{
            fontSize: 'clamp(1.05rem, 1.4vw, 1.25rem)',
            textShadow: '0 1px 12px rgba(0,0,0,0.25)'
          }}
          dangerouslySetInnerHTML={{ __html: section.content }} />


        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-10 sm:mb-12">
          {section.ctaLabel && section.ctaUrl &&
          <Link
            to={section.ctaUrl}
            className="btn-premium inline-flex items-center justify-center gap-2 bg-white text-[#05070d] hover:bg-white text-[15px]">

              {section.ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </Link>
          }
          {section.ctaLabel2 && section.ctaUrl2 &&
          <button
            type="button"
            onClick={openJobDeposit}
            className="inline-flex items-center justify-center gap-2 bg-white/[0.08] border border-white/25 text-white hover:bg-white/[0.14] px-7 py-[14px] rounded-xl font-bold text-[15px] transition-all backdrop-blur-md">

              {section.ctaLabel2}
            </button>
          }
        </div>

        {/* Search bar — glassmorphism premium */}
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-1.5 bg-white/[0.08] backdrop-blur-xl rounded-2xl p-2 max-w-3xl border border-white/20"
          style={{ boxShadow: '0 24px 60px -12px rgba(0,0,0,0.5)' }}>

          <div className="flex flex-1 items-center gap-2 px-4 py-2.5 bg-white/[0.06] rounded-xl border border-white/[0.06]">
            <Search className="w-4 h-4 text-white/60 flex-shrink-0" aria-hidden="true" />
            <input
              type="text"
              placeholder="Métier, mot-clé, diplôme…"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="flex-1 outline-none text-white placeholder-white/50 text-sm bg-transparent min-w-0"
              aria-label="Recherche par mot-clé" />

          </div>
          <div className="flex flex-1 items-center gap-2 px-4 py-2.5 bg-white/[0.06] rounded-xl border border-white/[0.06]">
            <MapPin className="w-4 h-4 text-white/60 flex-shrink-0" aria-hidden="true" />
            <input
              type="text"
              placeholder="Ville, département ou région"
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="flex-1 outline-none text-white placeholder-white/50 text-sm bg-transparent min-w-0"
              aria-label="Recherche par lieu" />

          </div>
          <button
            type="submit"
            className="bg-white text-[#05070d] hover:bg-white/90 px-6 py-2.5 rounded-xl font-bold text-sm transition-colors whitespace-nowrap">

            Rechercher
          </button>
        </form>
      </div>

      {/* ── Filet gradient signature BNSSA → MNS en bas du hero ── */}
      <div
        className="relative h-[2px] w-full"
        aria-hidden="true"
        style={{
          background:
          'linear-gradient(90deg, transparent 0%, rgba(220,38,38,0.85) 20%, rgba(217,70,239,0.7) 50%, rgba(37,99,235,0.85) 80%, transparent 100%)'
        }} />


      {/* Modal de dépôt d'annonce ouvert depuis le CTA « Déposer une annonce ». */}
      {jobDepositModal}
    </section>);

}